import React, { useRef } from "react";
import { Paperclip, X, FileText, FileSpreadsheet, FileImage, File as FileIcon } from "lucide-react";
import { toast } from "sonner";

const ALLOWED_MIMES = new Set([
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
]);
const ACCEPT_ATTR = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.csv,.zip,application/zip,application/x-zip-compressed";
export const MAX_FILES = 5;
export const MAX_BYTES = 15 * 1024 * 1024;

export function fmtSize(b) {
    if (b == null) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function truncate(s, n = 40) {
    if (!s) return "";
    return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function iconFor(mime) {
    if (!mime) return <FileIcon size={14} strokeWidth={2} />;
    if (mime.startsWith("image/")) return <FileImage size={14} strokeWidth={2} />;
    if (mime === "application/pdf") return <FileText size={14} strokeWidth={2} />;
    if (mime.includes("spreadsheet") || mime === "text/csv" || mime === "application/vnd.ms-excel")
        return <FileSpreadsheet size={14} strokeWidth={2} />;
    if (mime.includes("word") || mime === "application/msword")
        return <FileText size={14} strokeWidth={2} />;
    return <FileIcon size={14} strokeWidth={2} />;
}

/**
 * Bottone graffetta + preview compatta dei file selezionati.
 * Controlled: `files` + `onChange(newFiles)` gestiti dal parent.
 * Validation client-side: MIME whitelist, size max 15MB, count max 5.
 * Server-side comunque re-valida.
 */
export default function AttachmentPicker({ files, onChange, disabled, testIdPrefix = "attach" }) {
    const ref = useRef(null);

    const pickFiles = () => {
        if (disabled) return;
        if (files.length >= MAX_FILES) {
            toast.error(`Massimo ${MAX_FILES} file per messaggio`);
            return;
        }
        ref.current?.click();
    };

    const onFiles = (e) => {
        const picked = Array.from(e.target.files || []);
        // Reset input so re-selezione dello stesso file emetta change
        e.target.value = "";
        const next = [...files];
        for (const f of picked) {
            if (next.length >= MAX_FILES) {
                toast.error(`Massimo ${MAX_FILES} file per messaggio`);
                break;
            }
            if (!ALLOWED_MIMES.has(f.type)) {
                toast.error(`${f.name}: tipo non consentito`);
                continue;
            }
            if (f.size > MAX_BYTES) {
                toast.error(`${f.name}: dimensione massima 15MB`);
                continue;
            }
            // Dedupe by name+size
            if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
            next.push(f);
        }
        onChange(next);
    };

    const removeAt = (i) => {
        const next = files.slice();
        next.splice(i, 1);
        onChange(next);
    };

    return (
        <div className="lt-attach-picker" data-testid={`${testIdPrefix}-wrap`}>
            <input
                ref={ref}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                onChange={onFiles}
                style={{ display: "none" }}
                data-testid={`${testIdPrefix}-input`}
            />
            <button
                type="button"
                className="lt-btn-ghost lt-attach-picker__btn"
                onClick={pickFiles}
                disabled={disabled || files.length >= MAX_FILES}
                aria-label="Aggiungi file"
                title="Aggiungi file"
                data-testid={`${testIdPrefix}-btn`}
            >
                <Paperclip size={16} strokeWidth={2} />
            </button>

            {files.length > 0 && (
                <ul className="lt-attach-picker__list" data-testid={`${testIdPrefix}-list`}>
                    {files.map((f, i) => (
                        <li key={`${f.name}-${f.size}-${i}`} className="lt-attach-picker__item" data-testid={`${testIdPrefix}-item-${i}`}>
                            <span className="lt-attach-picker__icon">{iconFor(f.type)}</span>
                            <span className="lt-attach-picker__name" title={f.name}>{truncate(f.name)}</span>
                            <span className="lt-attach-picker__size">{fmtSize(f.size)}</span>
                            <button
                                type="button"
                                className="lt-attach-picker__remove"
                                onClick={() => removeAt(i)}
                                disabled={disabled}
                                aria-label={`Rimuovi ${f.name}`}
                                data-testid={`${testIdPrefix}-remove-${i}`}
                            >
                                <X size={13} strokeWidth={2.25} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
